/**
 * Instance axios centralisée pour tous les appels API.
 * Remplace les 22 occurrences de `const API = process.env.REACT_APP_BACKEND_URL`
 * dispersées dans les pages et composants.
 *
 * 29/05 — Refresh JWT automatique sur 401 (avant redirect login).
 * - Backend POST /api/auth/refresh emet un nouveau access_token via le
 *   refresh_token (7j de validite).
 * - Si refresh OK → rejoue la requete originale, transparent pour l'user.
 * - Si refresh KO (refresh_token expire / invalide) → redirect /login.
 * - Anti boucle : la requete /auth/refresh elle-meme n'est pas re-intercepted.
 * - Intercepteur AUSSI installe sur axios.defaults pour les fichiers qui
 *   utilisent `import axios from 'axios'` direct (ex: TaskContext, CRMPage).
 */
import axios from 'axios';

const baseURL = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({
  baseURL,
  withCredentials: true,
});

// State partage entre les 2 intercepteurs : une seule tentative de refresh
// concurrente (sinon 555 requetes 401 = 555 refresh en parallele).
let refreshPromise = null;
const REFRESH_URL = `${baseURL}/api/auth/refresh`;

async function tryRefresh() {
  if (!refreshPromise) {
    refreshPromise = axios.post(REFRESH_URL, {}, { withCredentials: true })
      .then(() => true)
      .catch(() => false)
      .finally(() => { setTimeout(() => { refreshPromise = null; }, 1000); });
  }
  return refreshPromise;
}

// 01/06 HOTFIX Theo (cron E81 + faille signature) — pages publiques qui ne
// doivent JAMAIS rediriger vers /login meme si une requete annexe renvoie 401.
// Sinon : un agent qui clique sur son lien mail signature /signer/<token>
// (route publique) est kicke vers /login parce que AuthContext.js fait au
// mount GET /api/auth/me qui renvoie 401 (non logge) -> intercepteur redirect.
const PUBLIC_PATH_PREFIXES = ['/login', '/forgot-password', '/reset-password', '/signer/'];

function isOnPublicPage() {
  const path = window.location.pathname || '';
  return PUBLIC_PATH_PREFIXES.some(p => path === p || path.startsWith(p));
}

// Endpoints qui ne doivent PAS declencher de redirect login meme sur 401
// (sont juste des checks d'etat, l'absence d'auth est normale).
const SILENT_401_URLS = ['/auth/me'];

// 17/09 — LE CADENAS FONDATEUR. Le serveur répond 423 Locked quand on tente de
// modifier une pièce cadenassée (backend/utils/cadenas.py). Ici, deux gestes :
//   1. on émet `cadenas:verrouille` — la popup « Fichier verrouillé » s'ouvre seule,
//      où que soit l'écran, sans qu'il ait à connaître le cadenas ;
//   2. on rabat `detail` (un objet) sur sa phrase, pour que les écrans qui affichent
//      `err.response.data.detail` dans un toast montrent une phrase et non [object Object].
// L'événement est émis en direct (pas d'import du composant) : api.js ne doit
// dépendre d'aucun composant, sinon cycle d'import.
function traiterCadenas(error) {
  if (error.response?.status !== 423) return;
  const detail = error.response?.data?.detail;
  if (detail && typeof detail === 'object') {
    try {
      window.dispatchEvent(new CustomEvent('cadenas:verrouille', { detail }));
    } catch { /* pas de CustomEvent : le toast de l'écran suffit */ }
    error.response.data.detail = detail.message || 'Document verrouillé par le cadenas fondateur.';
    error.response.data.cadenas = detail;
  }
}

function makeInterceptor(axiosInstance) {
  return async (error) => {
    const status = error.response?.status;
    const url = error.config?.url || '';
    traiterCadenas(error);
    // Anti-boucle : n'intercepte pas le refresh lui-meme ni le login
    if (status === 401 && !url.includes('/auth/refresh') && !url.includes('/auth/login')) {
      // Si on est sur une page publique, on ne tente RIEN. L'app a le droit
      // de tester /auth/me ou autre — 401 = "pas logge" est l'etat attendu.
      if (isOnPublicPage()) {
        return Promise.reject(error);
      }
      // Endpoints "check d'etat" : on rejette silencieusement sans redirect
      // (AuthContext va lire l'erreur et passer en mode anonyme).
      if (SILENT_401_URLS.some(u => url.includes(u))) {
        return Promise.reject(error);
      }
      // Marker pour eviter de retry plusieurs fois la meme requete
      if (!error.config._retried) {
        error.config._retried = true;
        const ok = await tryRefresh();
        if (ok) {
          // Rejoue la requete originale avec le nouveau access_token
          return axiosInstance(error.config);
        }
      }
      // Refresh KO ou deja retried → redirect login
      if (!isOnPublicPage()) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  };
}

api.interceptors.response.use((response) => response, makeInterceptor(api));

// 29/05 — Intercepteur global pour les fichiers qui utilisent axios direct
// (TaskContext, CRMPage, etc.) — sinon 401 spam silencieux.
axios.interceptors.response.use((response) => response, makeInterceptor(axios));

export const API_URL = baseURL;
export default api;
